import React, { useState, useEffect, useRef, useCallback, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { HugeiconsIcon } from '@hugeicons/react';
import {
  DocumentValidationIcon,
  File01Icon,
  Wallet01Icon,
  AccountSetting01Icon,
  Notification01Icon,
  Add01Icon,
  ArrowLeft01Icon,
  GlobeIcon,
  Camera01Icon,
  Logout01Icon,
} from '@hugeicons/core-free-icons';
import { SaaeLogo } from '@components/SaaeLogo';
import { StaggerKeyProvider } from '@contexts/StaggerKeyContext';

/** Shown when the avatar modal chunk fails to load (e.g. 504 Outdated Optimize Dep). */
function AvatarUploadModalFallback({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}) {
  const { t } = useTranslation();
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
      <div className="rounded-2xl bg-surface-cards p-6 shadow-lg max-w-sm text-center">
        <p className="text-primary font-sans mb-4">
          {t('profile.avatar.loadFailed', { defaultValue: 'Could not load avatar upload. Please refresh the page.' })}
        </p>
        <button
          type="button"
          onClick={onClose}
          className="rounded-[16px] bg-primary px-4 py-2 text-white font-medium"
        >
          {t('common.close', { defaultValue: 'Close' })}
        </button>
      </div>
    </div>
  );
}

const AvatarUploadModal = React.lazy(() =>
  import('@components/AvatarUploadModal/AvatarUploadModal')
    .then((m) => ({ default: m.AvatarUploadModal }))
    .catch((err) => {
      console.error('AvatarUploadModal load failed', err);
      return { default: AvatarUploadModalFallback };
    })
);
import { useLanguage } from '@hooks/useLanguage';
import { getProfile } from '@services/api';
import { apiClient } from '@services/apiClient';
import { springSidebar, pageTransition } from '@utils/motionConfig';
import { RtlDirectionalIcon } from '@utils/rtlIcons';
import avatarDefault from '../public/images/avatardefault.png';

const NAV_ITEMS = [
  { id: 'main' as const, labelKey: 'dashboard.mainBrokerage', icon: DocumentValidationIcon },
  { id: 'sub' as const, labelKey: 'dashboard.subBrokerage', icon: File01Icon },
  { id: 'wallet' as const, labelKey: 'dashboard.wallet', icon: Wallet01Icon },
  { id: 'settings' as const, labelKey: 'dashboard.settings', icon: AccountSetting01Icon },
];

const SIDEBAR_WIDTH_EXPANDED = 240;
const SIDEBAR_WIDTH_COLLAPSED = 72;
export const SIDEBAR_BREAKPOINT_PX = 720;

function useIsNarrow(breakpointPx: number) {
  const [isNarrow, setIsNarrow] = useState(false);
  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${breakpointPx - 1}px)`);
    const handler = () => setIsNarrow(mql.matches);
    handler();
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [breakpointPx]);
  return isNarrow;
}

export type BrokerageNavId = 'main' | 'sub' | 'wallet' | 'settings';

export interface BrokerageLayoutProps {
  pageTitle: string;
  activeNavId: BrokerageNavId;
  onNavSelect: (id: BrokerageNavId) => void;
  onBack?: () => void;
  onAddBrokerage?: () => void;
  /** Controlled sidebar collapsed state; when provided, persists across page changes. */
  sidebarCollapsed?: boolean;
  onSidebarCollapsedChange?: (collapsed: boolean) => void;
  /** Optional key for the main content area; when set, avoids reusing the same slot across different page types (e.g. list vs detail). */
  contentKey?: string;
  /** Called when user chooses Sign out from the avatar menu. */
  onSignOut?: () => void;
  children: React.ReactNode;
}

export function BrokerageLayout({
  pageTitle,
  activeNavId,
  onNavSelect,
  onBack,
  onAddBrokerage,
  sidebarCollapsed: controlledCollapsed,
  onSidebarCollapsedChange,
  contentKey,
  onSignOut,
  children,
}: BrokerageLayoutProps) {
  const isNarrow = useIsNarrow(SIDEBAR_BREAKPOINT_PX);
  const [internalCollapsed, setInternalCollapsed] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < SIDEBAR_BREAKPOINT_PX
  );
  const isControlled = controlledCollapsed !== undefined && onSidebarCollapsedChange !== undefined;
  const isSidebarCollapsed = isControlled ? controlledCollapsed : internalCollapsed;
  const setSidebarCollapsed = isControlled ? onSidebarCollapsedChange : setInternalCollapsed;

  useEffect(() => {
    if (isNarrow) setSidebarCollapsed(true);
  }, [isNarrow, setSidebarCollapsed]);

  const { t, i18n } = useTranslation();
  const { language, toggleLanguage } = useLanguage();
  const isRtl = i18n.language.startsWith('ar');
  const isOverlay = isNarrow && !isSidebarCollapsed;
  const sidebarWidth = isSidebarCollapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED;
  const mainMarginStart = isOverlay ? 0 : sidebarWidth;

  const [headerProfile, setHeaderProfile] = useState<{ name: string; nameAr?: string; email: string; avatarUrl?: string | null } | null>(null);
  const [avatarMenuOpen, setAvatarMenuOpen] = useState(false);
  const [avatarModalOpen, setAvatarModalOpen] = useState(false);
  const [avatarVersion, setAvatarVersion] = useState(0);
  const avatarMenuRef = useRef<HTMLDivElement>(null);

  const refreshProfile = useCallback(() => {
    getProfile()
      .then(({ data }) => {
        setHeaderProfile({ name: data.name, nameAr: data.nameAr, email: data.email, avatarUrl: data.avatarUrl ?? null });
        setAvatarVersion((v) => v + 1);
      })
      .catch(() => {});
  }, []);

  const [avatarBlobUrl, setAvatarBlobUrl] = useState<string | null>(null);
  const avatarBlobUrlRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getProfile()
      .then(({ data }) => {
        if (!cancelled) setHeaderProfile({ name: data.name, nameAr: data.nameAr, email: data.email, avatarUrl: data.avatarUrl ?? null });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!headerProfile?.avatarUrl) {
      if (avatarBlobUrlRef.current) {
        URL.revokeObjectURL(avatarBlobUrlRef.current);
        avatarBlobUrlRef.current = null;
      }
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: clear blob URL when avatar is removed
      setAvatarBlobUrl(null);
      return;
    }
    let cancelled = false;
    const avatarRequestUrl = headerProfile.avatarUrl?.startsWith('/') ? headerProfile.avatarUrl : '/api/profile/avatar';
    apiClient
      .get<Blob>(avatarRequestUrl, { responseType: 'blob' })
      .then((res) => {
        if (!cancelled && res.data) {
          if (avatarBlobUrlRef.current) URL.revokeObjectURL(avatarBlobUrlRef.current);
          const url = URL.createObjectURL(res.data);
          avatarBlobUrlRef.current = url;
          setAvatarBlobUrl(url);
        }
      })
      .catch(() => {
        if (!cancelled) setAvatarBlobUrl(null);
      });
    return () => { cancelled = true; };
  }, [headerProfile?.avatarUrl, avatarVersion]);

  useEffect(() => {
    return () => {
      if (avatarBlobUrlRef.current) {
        URL.revokeObjectURL(avatarBlobUrlRef.current);
        avatarBlobUrlRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!avatarMenuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (avatarMenuRef.current && !avatarMenuRef.current.contains(e.target as Node)) {
        setAvatarMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [avatarMenuOpen]);

  return (
    <div className="flex h-full overflow-hidden bg-surface-web font-sans">
      {isOverlay && (
        <div
          role="presentation"
          className="fixed inset-0 z-40 bg-black/20 transition-opacity duration-300 ease-in-out"
          onClick={() => setSidebarCollapsed(true)}
          onKeyDown={(e) => e.key === 'Escape' && setSidebarCollapsed(true)}
          aria-hidden
        />
      )}
      <motion.aside
        className={`fixed start-0 top-0 z-50 flex h-screen flex-col overflow-hidden border-e border-surface-border bg-surface-cards ${
          isOverlay ? 'shadow-xl' : ''
        }`}
        initial={false}
        animate={{ width: sidebarWidth }}
        transition={springSidebar}
        aria-label="Main navigation"
        aria-expanded={!isSidebarCollapsed}
      >
        <div className={`flex justify-center px-4 py-6 ${isSidebarCollapsed ? 'px-2' : ''}`}>
          {isSidebarCollapsed ? (
            <SaaeLogo variant="icon" mode="light" className="shrink-0" />
          ) : (
            <SaaeLogo size="small" mode="light" className="!h-[30.6px]" />
          )}
        </div>
        <nav className="flex flex-1 flex-col gap-1 px-2 py-4">
          {NAV_ITEMS.map((item) => {
            const active = activeNavId === item.id;
            const label = t(item.labelKey);
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => onNavSelect(item.id)}
                title={label}
                className={`flex min-h-[48px] w-full items-center gap-2 rounded-[16px] py-2 text-start text-sm font-medium transition-colors ${
                  isSidebarCollapsed ? 'justify-center px-0' : 'px-4'
                } ${
                  active ? 'bg-primary text-white' : 'text-primary/70 hover:bg-surface-gray-lighter hover:text-primary'
                }`}
                aria-current={active ? 'page' : undefined}
                aria-label={label}
              >
                <HugeiconsIcon icon={item.icon} size={20} className={`shrink-0 ${active ? 'text-white' : 'text-primary/60'}`} />
                {!isSidebarCollapsed && <span className="truncate">{label}</span>}
              </button>
            );
          })}
        </nav>
        <div className={`px-2 pb-2 pt-0 ${isSidebarCollapsed ? 'px-2' : 'px-4'}`}>
          <button
            type="button"
            onClick={() => setSidebarCollapsed(!isSidebarCollapsed)}
            className={`flex w-full items-center justify-center gap-2 py-2.5 text-secondary transition-all duration-300 ease-in-out focus:outline-none ${
              isSidebarCollapsed
                ? 'rounded-[16px] border border-surface-border bg-surface-gray-lighter/50 px-0 hover:bg-secondary/20 hover:border-secondary/40 focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-2'
                : 'rounded-[16px] border-0 bg-transparent px-4 hover:bg-secondary/20 focus-visible:ring-0'
            }`}
            aria-label={isSidebarCollapsed ? t('dashboard.sidebarExpand') : t('dashboard.sidebarCollapse')}
          >
            <motion.span
              animate={{ rotate: isSidebarCollapsed ? 180 : 0 }}
              transition={springSidebar}
              className="inline-flex shrink-0"
            >
              <RtlDirectionalIcon>
                <HugeiconsIcon icon={ArrowLeft01Icon} size={20} />
              </RtlDirectionalIcon>
            </motion.span>
            {!isSidebarCollapsed && <span className="text-sm font-medium font-sans">{t('dashboard.sidebarCollapse')}</span>}
          </button>
        </div>
        <div className={`p-4 transition-all duration-300 ease-in-out ${isSidebarCollapsed ? 'px-2' : ''}`}>
          {isSidebarCollapsed ? (
            <button
              type="button"
              title={t('dashboard.addBrokerage')}
              onClick={() => onAddBrokerage?.()}
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[16px] border border-primary/20 bg-transparent text-primary transition-all duration-300 ease-in-out hover:bg-primary/10 hover:border-primary/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            >
              <HugeiconsIcon icon={Add01Icon} size={20} />
            </button>
          ) : (
            <div className="flex flex-col gap-4 rounded-card bg-gradient-primary p-5 transition-all duration-300 ease-in-out">
              <h3 className="text-center text-lg font-semibold text-white font-sans">{t('dashboard.startNow')}</h3>
              <button
                type="button"
                onClick={() => onAddBrokerage?.()}
                className="flex w-full items-center justify-center gap-2 rounded-[16px] bg-white py-3 px-4 font-medium text-sm text-primary transition-colors hover:bg-white/95 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 font-sans"
              >
                <HugeiconsIcon icon={Add01Icon} size={18} className="text-primary shrink-0" />
                {t('dashboard.addBrokerage')}
              </button>
            </div>
          )}
        </div>
      </motion.aside>

      <motion.main
        className="flex h-full min-h-0 min-w-0 flex-1 flex-col bg-surface-web"
        style={{ marginInlineStart: mainMarginStart }}
        initial={false}
        animate={{ marginInlineStart: mainMarginStart }}
        transition={springSidebar}
      >
        <header className="flex shrink-0 items-center justify-between border-b border-surface-border bg-surface-cards ps-6 pe-6 py-4 sm:ps-8 sm:pe-8">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            {onBack && (
              <button
                type="button"
                onClick={onBack}
                className="flex shrink-0 items-center justify-center rounded-[16px] p-2 text-primary/70 transition-colors hover:bg-surface-gray-lighter hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                aria-label={t('common.back')}
              >
                <RtlDirectionalIcon>
                  <HugeiconsIcon icon={ArrowLeft01Icon} size={20} />
                </RtlDirectionalIcon>
              </button>
            )}
            <h1 className="truncate text-lg font-bold text-primary font-sans sm:text-xl">{pageTitle}</h1>
          </div>
          <div className="flex items-center gap-4 sm:gap-6">
            <button
              type="button"
              onClick={toggleLanguage}
              className="flex items-center gap-2 rounded-[16px] border border-primary/30 px-3 py-2 text-sm font-medium text-primary transition-colors hover:bg-surface-primary-lighter"
              aria-label={t('common.language')}
              title={language === 'ar' ? t('common.english') : t('common.arabic')}
            >
              <HugeiconsIcon icon={GlobeIcon} size={20} className="shrink-0" />
              <span>{language === 'ar' ? t('common.english') : t('common.arabic')}</span>
            </button>
            <button
              type="button"
              className="rounded-[16px] p-2 text-primary/70 transition-colors hover:bg-surface-gray-lighter hover:text-primary"
              aria-label={t('dashboard.notifications')}
            >
              <HugeiconsIcon icon={Notification01Icon} size={24} />
            </button>
            <div className="relative flex items-center gap-3 sm:gap-4" ref={avatarMenuRef}>
              <button
                type="button"
                onClick={() => setAvatarMenuOpen((open) => !open)}
                className="flex items-center gap-3 sm:gap-4 rounded-[16px] p-1 pr-2 transition-colors hover:bg-surface-primary-lighter focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                aria-expanded={avatarMenuOpen}
                aria-haspopup="true"
                aria-label={t('dashboard.userProfile')}
              >
                <div className="aspect-square size-9 shrink-0 overflow-hidden rounded-full border border-surface-gray-darker bg-surface-primary-lighter sm:size-10">
                  <img
                    src={avatarBlobUrl ?? avatarDefault}
                    alt=""
                    width={40}
                    height={40}
                    className="size-full object-cover object-center"
                  />
                </div>
                <div className="flex flex-col items-start text-start min-w-0" dir={isRtl ? 'rtl' : 'ltr'}>
                  <span className="text-sm font-medium text-primary font-sans truncate" title={(isRtl ? (headerProfile?.nameAr || headerProfile?.name) : headerProfile?.name) ?? undefined}>
                    {(isRtl ? (headerProfile?.nameAr || headerProfile?.name) : headerProfile?.name) ?? '…'}
                  </span>
                  <span className="text-sm text-primary/60 truncate" title={headerProfile?.email ?? undefined}>
                    {headerProfile?.email || '…'}
                  </span>
                </div>
              </button>
              {avatarMenuOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.15 }}
                  className="absolute end-0 top-full z-50 mt-2 min-w-[12rem] rounded-[16px] border border-surface-border bg-surface-cards py-2 shadow-lg"
                  role="menu"
                >
                  <button
                    type="button"
                    role="menuitem"
                    className="flex w-full items-center gap-2 px-4 py-2.5 text-start text-sm font-medium text-primary font-sans transition-colors hover:bg-surface-primary-lighter"
                    onClick={() => {
                      setAvatarMenuOpen(false);
                      setAvatarModalOpen(true);
                    }}
                  >
                    <HugeiconsIcon icon={Camera01Icon} size={20} className="shrink-0 text-primary/70" />
                    {t('dashboard.changeAvatar', { defaultValue: 'Change avatar' })}
                  </button>
                  {onSignOut && (
                    <button
                      type="button"
                      role="menuitem"
                      className="flex w-full items-center gap-2 px-4 py-2.5 text-start text-sm font-medium text-primary font-sans transition-colors hover:bg-surface-primary-lighter"
                      onClick={() => {
                        setAvatarMenuOpen(false);
                        onSignOut();
                      }}
                    >
                      <HugeiconsIcon icon={Logout01Icon} size={20} className="shrink-0 text-primary/70" />
                      {t('dashboard.signOut', { defaultValue: 'Sign out' })}
                    </button>
                  )}
                </motion.div>
              )}
            </div>
          </div>
        </header>
        {avatarModalOpen && (
          <Suspense fallback={null}>
            <AvatarUploadModal
              open={avatarModalOpen}
              onClose={() => setAvatarModalOpen(false)}
              onSuccess={refreshProfile}
            />
          </Suspense>
        )}
        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden p-6 sm:p-8">
          <motion.div
            key={contentKey ?? activeNavId}
            initial={pageTransition.initial}
            animate={pageTransition.animate}
            transition={pageTransition.transition}
            className="h-full min-h-0"
          >
            <StaggerKeyProvider value={activeNavId}>
              {children}
            </StaggerKeyProvider>
          </motion.div>
        </div>
      </motion.main>
    </div>
  );
}
